import React, {useState, useMemo} from "react";
import {
  Card,
  Row,
  Col,
  ProgressBar,
  Button,
  Badge,
  Modal,
  Alert,
  Tabs,
  Tab,
} from "react-bootstrap";
import {
  FaPoll,
  FaUsers,
  FaDownload,
  FaEye,
  FaClock,
  FaTrophy,
} from "react-icons/fa";
import {Doughnut, Bar} from "react-chartjs-2";
import * as XLSX from "xlsx";

const PollResults = ({
  poll,
  campaignName,
  responses = [],
  demographics = {},
  showModal = false,
  onHide = () => {},
  detailed = false,
}) => {
  const [showDemographics, setShowDemographics] = useState(false);

  // Generar datos mock más realistas para la encuesta
  const mockPollData = useMemo(() => {
    if (!poll) return null;

    const totalResponses = Math.floor(Math.random() * 300 + 150);
    const responseRate = (65 + Math.random() * 25).toFixed(1);

    const responses = poll.options.map((option, index) => {
      const count =
        index === 0
          ? Math.floor(totalResponses * 0.4) // Primera opción más popular
          : index === 1
          ? Math.floor(totalResponses * 0.3)
          : Math.floor((totalResponses * 0.3) / (poll.options.length - 2));

      return {
        option,
        count,
        percentage: ((count / totalResponses) * 100).toFixed(1),
        trend: Math.floor(Math.random() * 20 - 10), // -10 a +10
        demographics: {
          byOrganization: [
            {name: "Transportes González", count: Math.floor(count * 0.3)},
            {name: "Logística Andina", count: Math.floor(count * 0.25)},
            {name: "Carga Express", count: Math.floor(count * 0.2)},
            {name: "Otros", count: Math.floor(count * 0.25)},
          ],
          byTimeOfResponse: {
            morning: Math.floor(count * 0.4),
            afternoon: Math.floor(count * 0.35),
            evening: Math.floor(count * 0.25),
          },
        },
      };
    });

    return {
      question: poll.question,
      totalResponses,
      responseRate,
      responses,
      avgResponseTime: `${(2 + Math.random() * 4).toFixed(1)} horas`,
      completionRate: (85 + Math.random() * 10).toFixed(1),
      topResponse: responses.reduce(
        (max, r) => (r.count > max.count ? r : max),
        responses[0]
      ),
      responsesByHour: Array.from({length: 24}, (_, hour) => ({
        hour,
        count: Math.floor(Math.random() * (totalResponses / 12)),
      })),
    };
  }, [poll]);

  if (!poll || !mockPollData) {
    return (
      <Alert variant="info">
        <FaPoll className="me-2" />
        No hay datos de encuesta disponibles.
      </Alert>
    );
  }

  // Configuración del gráfico de dona
  const doughnutData = {
    labels: mockPollData.responses.map((r) => r.option),
    datasets: [
      {
        data: mockPollData.responses.map((r) => r.count),
        backgroundColor: [
          "rgba(34, 197, 94, 0.8)",
          "rgba(59, 130, 246, 0.8)",
          "rgba(249, 115, 22, 0.8)",
          "rgba(239, 68, 68, 0.8)",
          "rgba(168, 85, 247, 0.8)",
          "rgba(236, 72, 153, 0.8)",
        ],
        borderWidth: 0,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 20,
          usePointStyle: true,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.parsed;
            const percentage = (
              (value / mockPollData.totalResponses) *
              100
            ).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  // Configuración del gráfico de barras por hora
  const hourlyData = {
    labels: mockPollData.responsesByHour.map((h) => `${h.hour}:00`),
    datasets: [
      {
        label: "Respuestas por Hora",
        data: mockPollData.responsesByHour.map((h) => h.count),
        backgroundColor: "rgba(59, 130, 246, 0.8)",
        borderRadius: 4,
      },
    ],
  };

  const hourlyOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.1)",
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 12,
        },
      },
    },
  };

  const handleExport = () => {
    const exportData = [
      {Campo: "Pregunta", Valor: mockPollData.question},
      {Campo: "Total Respuestas", Valor: mockPollData.totalResponses},
      {Campo: "Tasa de Respuesta", Valor: mockPollData.responseRate + "%"},
      {Campo: "Tiempo Promedio", Valor: mockPollData.avgResponseTime},
      {Campo: "", Valor: ""}, // Separador
      ...mockPollData.responses.map((r) => ({
        Campo: r.option,
        Valor: `${r.count} (${r.percentage}%)`,
      })),
    ];

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Resultados");
    XLSX.writeFile(
      workbook,
      `Encuesta_${campaignName?.replace(/\s+/g, "_") || "Resultados"}.xlsx`
    );
  };

  // Vista compacta para usar en cards
  if (!detailed && !showModal) {
    return (
      <Card className="shadow-sm border-0 h-100">
        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
          <div>
            <h6 className="fw-bold mb-1">{poll.question}</h6>
            {campaignName && (
              <small className="text-muted">{campaignName}</small>
            )}
          </div>
          <Badge bg="info">
            <FaUsers className="me-1" />
            {mockPollData.totalResponses}
          </Badge>
        </Card.Header>
        <Card.Body>
          <div className="mb-3" style={{height: "200px"}}>
            <Doughnut data={doughnutData} options={doughnutOptions} />
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Tasa de respuesta: <strong>{mockPollData.responseRate}%</strong>
            </small>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => setShowDemographics(true)}
            >
              <FaEye className="me-1" />
              Ver Detalles
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  // Vista detallada en modal
  return (
    <Modal
      show={showModal || showDemographics}
      onHide={() => {
        onHide();
        setShowDemographics(false);
      }}
      size="xl"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FaPoll className="me-2 text-primary" />
          Resultados Detallados de Encuesta
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Header con métricas principales */}
        <Row className="mb-4">
          <Col md={8}>
            <h5 className="fw-bold text-primary mb-2">
              {mockPollData.question}
            </h5>
            {campaignName && (
              <p className="text-muted mb-0">Campaña: {campaignName}</p>
            )}
          </Col>
          <Col md={4} className="text-end">
            <Button variant="outline-success" size="sm" onClick={handleExport}>
              <FaDownload className="me-2" />
              Exportar Resultados
            </Button>
          </Col>
        </Row>

        {/* KPIs */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center border-0 bg-light">
              <Card.Body className="py-3">
                <div className="fs-4 fw-bold text-primary">
                  {mockPollData.totalResponses}
                </div>
                <div className="small text-muted">Total Respuestas</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center border-0 bg-light">
              <Card.Body className="py-3">
                <div className="fs-4 fw-bold text-success">
                  {mockPollData.responseRate}%
                </div>
                <div className="small text-muted">Tasa de Respuesta</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center border-0 bg-light">
              <Card.Body className="py-3">
                <div className="fs-4 fw-bold text-info">
                  {mockPollData.avgResponseTime}
                </div>
                <div className="small text-muted">Tiempo Promedio</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center border-0 bg-light">
              <Card.Body className="py-3">
                <div className="fs-4 fw-bold text-warning">
                  {mockPollData.completionRate}%
                </div>
                <div className="small text-muted">Completitud</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Tabs defaultActiveKey="results" className="mb-4">
          {/* Tab de Resultados */}
          <Tab eventKey="results" title="Resultados">
            <Row>
              <Col lg={6}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white">
                    <h6 className="fw-bold mb-0">Distribución de Respuestas</h6>
                  </Card.Header>
                  <Card.Body style={{height: "300px"}}>
                    <Doughnut data={doughnutData} options={doughnutOptions} />
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white">
                    <h6 className="fw-bold mb-0">Detalle por Opción</h6>
                  </Card.Header>
                  <Card.Body>
                    {mockPollData.responses.map((response, index) => (
                      <div key={index} className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center">
                            <span className="fw-bold me-2">
                              {response.option}
                            </span>
                            {index === 0 && (
                              <Badge bg="warning" className="me-2">
                                <FaTrophy className="me-1" />
                                Más Popular
                              </Badge>
                            )}
                          </div>
                          <div className="text-end">
                            <div className="fw-bold">{response.count}</div>
                            <small className="text-muted">
                              ({response.percentage}%)
                            </small>
                          </div>
                        </div>
                        <ProgressBar
                          now={parseFloat(response.percentage)}
                          style={{height: "10px"}}
                          variant={
                            index === 0
                              ? "success"
                              : index === 1
                              ? "primary"
                              : "info"
                          }
                        />
                        {response.trend !== 0 && (
                          <small
                            className={`text-${
                              response.trend > 0 ? "success" : "danger"
                            }`}
                          >
                            {response.trend > 0 ? "↗" : "↘"}{" "}
                            {Math.abs(response.trend)}% vs. promedio
                          </small>
                        )}
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          {/* Tab de Análisis Temporal */}
          <Tab eventKey="timing" title="Análisis Temporal">
            <Row>
              <Col lg={8}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white">
                    <h6 className="fw-bold mb-0">
                      Respuestas por Hora del Día
                    </h6>
                  </Card.Header>
                  <Card.Body style={{height: "300px"}}>
                    <Bar data={hourlyData} options={hourlyOptions} />
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white">
                    <h6 className="fw-bold mb-0">Insights Temporales</h6>
                  </Card.Header>
                  <Card.Body>
                    <Alert variant="success" className="mb-3">
                      <FaClock className="me-2" />
                      <strong>Pico de Respuestas:</strong>
                      <br />
                      10:00 - 12:00 hrs
                    </Alert>

                    <div className="mb-3">
                      <h6 className="fw-bold">Distribución por Período</h6>
                      <div className="mb-2">
                        <div className="d-flex justify-content-between">
                          <span>Mañana (6-12h)</span>
                          <span className="fw-bold">
                            {mockPollData.responses.reduce(
                              (sum, r) =>
                                sum + r.demographics.byTimeOfResponse.morning,
                              0
                            )}
                          </span>
                        </div>
                        <ProgressBar
                          now={40}
                          variant="success"
                          style={{height: "6px"}}
                        />
                      </div>

                      <div className="mb-2">
                        <div className="d-flex justify-content-between">
                          <span>Tarde (12-18h)</span>
                          <span className="fw-bold">
                            {mockPollData.responses.reduce(
                              (sum, r) =>
                                sum + r.demographics.byTimeOfResponse.afternoon,
                              0
                            )}
                          </span>
                        </div>
                        <ProgressBar
                          now={35}
                          variant="primary"
                          style={{height: "6px"}}
                        />
                      </div>

                      <div className="mb-2">
                        <div className="d-flex justify-content-between">
                          <span>Noche (18-24h)</span>
                          <span className="fw-bold">
                            {mockPollData.responses.reduce(
                              (sum, r) =>
                                sum + r.demographics.byTimeOfResponse.evening,
                              0
                            )}
                          </span>
                        </div>
                        <ProgressBar
                          now={25}
                          variant="warning"
                          style={{height: "6px"}}
                        />
                      </div>
                    </div>

                    <Alert variant="info">
                      <strong>Recomendación:</strong>
                      <br />
                      Las encuestas enviadas en horario matutino obtienen mejor
                      tasa de respuesta.
                    </Alert>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          {/* Tab de Demografía */}
          <Tab eventKey="demographics" title="Demografía">
            <Row>
              {mockPollData.responses.map((response, index) => (
                <Col lg={6} key={index} className="mb-4">
                  <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white">
                      <h6 className="fw-bold mb-0">"{response.option}"</h6>
                      <small className="text-muted">
                        {response.count} respuestas
                      </small>
                    </Card.Header>
                    <Card.Body>
                      <h6 className="fw-bold mb-3">Por Organización</h6>
                      {response.demographics.byOrganization.map(
                        (org, orgIndex) => (
                          <div key={orgIndex} className="mb-2">
                            <div className="d-flex justify-content-between mb-1">
                              <span className="small">{org.name}</span>
                              <span className="small fw-bold">{org.count}</span>
                            </div>
                            <ProgressBar
                              now={(org.count / response.count) * 100}
                              style={{height: "6px"}}
                              variant={
                                orgIndex === 0
                                  ? "success"
                                  : orgIndex === 1
                                  ? "primary"
                                  : "info"
                              }
                            />
                          </div>
                        )
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => {
            onHide();
            setShowDemographics(false);
          }}
        >
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PollResults;
